import React, { useEffect, useState } from 'react';
import { Clock, ZoomIn, ChevronLeft, ChevronRight, X } from 'lucide-react';
interface LabelImage {
  id: string;
  imageUrl: string;
  timestamp: string;
  barrelId: string;
}
// Beispieldaten für die Demonstration mit dem neuen Bild
const sampleLabelImages: LabelImage[] = [{
  id: 'img1',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6Pcpqeo2YvZ8JTh9MsXHAi/Omni_200_200l_Metallfass-1.jpg',
  timestamp: '08:23:15',
  barrelId: 'FB-2023-45892'
}, {
  id: 'img2',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6Pcpqeo2YvZ8JTh9MsXHAi/Omni_200_200l_Metallfass-1.jpg',
  timestamp: '09:42:38',
  barrelId: 'FB-2023-45893'
}, {
  id: 'img3',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6Pcpqeo2YvZ8JTh9MsXHAi/Omni_200_200l_Metallfass-1.jpg',
  timestamp: '10:15:22',
  barrelId: 'FB-2023-45894'
}, {
  id: 'img4',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6Pcpqeo2YvZ8JTh9MsXHAi/Omni_200_200l_Metallfass-1.jpg',
  timestamp: '11:37:05',
  barrelId: 'FB-2023-45895'
}, {
  id: 'img5',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6Pcpqeo2YvZ8JTh9MsXHAi/Omni_200_200l_Metallfass-1.jpg',
  timestamp: '13:08:59',
  barrelId: 'FB-2023-45896'
}, {
  id: 'img6',
  imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6Pcpqeo2YvZ8JTh9MsXHAi/Omni_200_200l_Metallfass-1.jpg',
  timestamp: '14:45:33',
  barrelId: 'FB-2023-45897'
}];
interface BarrelLabelImagesProps {
  date?: string; // Optional date parameter, defaults to current date
}
export const BarrelLabelImages: React.FC<BarrelLabelImagesProps> = ({
  date
}) => {
  const currentDate = date || new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const [selectedImage, setSelectedImage] = useState<LabelImage | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedImage]);
  const handleImageClick = (image: LabelImage) => {
    const index = sampleLabelImages.findIndex(img => img.id === image.id);
    setSelectedImage(image);
    setSelectedIndex(index);
  };
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedImage(sampleLabelImages[newIndex]);
    }
  };
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedIndex < sampleLabelImages.length - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedImage(sampleLabelImages[newIndex]);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrevImage(e as unknown as React.MouseEvent);
    } else if (e.key === 'ArrowRight') {
      handleNextImage(e as unknown as React.MouseEvent);
    } else if (e.key === 'Escape') {
      setSelectedImage(null);
      setSelectedIndex(-1);
    }
  };
  const closeModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImage(null);
    setSelectedIndex(-1);
  };
  return <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-3">
      <div className="flex items-center justify-between mb-4" data-id="element-4">
        <h3 className="text-xl font-medium text-gray-200" data-id="element-5">
          Erfasste Fass-Etiketten
        </h3>
        <div className="flex items-center gap-2 text-gray-400" data-id="element-6">
          <Clock size={16} data-id="element-7" />
          <span data-id="element-8">{currentDate}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4" data-id="element-9">
        {sampleLabelImages.map(image => <div key={image.id} className="relative bg-gray-700 rounded-lg overflow-hidden cursor-pointer group" onClick={() => handleImageClick(image)} data-id="element-10">
            <img src={image.imageUrl} alt={`Fass-Etikett ${image.barrelId}`} className="w-full h-48 object-cover" data-id="element-11" />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 p-2" data-id="element-12">
              <div className="flex justify-between items-center" data-id="element-13">
                <span className="text-white font-medium" data-id="element-14">{image.barrelId}</span>
                <span className="text-gray-300 text-sm" data-id="element-15">{image.timestamp}</span>
              </div>
            </div>
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-16">
              <div className="bg-blue-500 rounded-full p-2" data-id="element-17">
                <ZoomIn size={20} className="text-white" data-id="element-18" />
              </div>
            </div>
          </div>)}
      </div>
      {/* Modal für Bildvergrößerung mit Navigation */}
      {selectedImage && <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center" onClick={closeModal} onKeyDown={handleKeyDown} tabIndex={0} style={{
      position: 'fixed',
      zIndex: 9999999,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      isolation: 'isolate'
    }} data-id="element-19">
          <div className="max-w-3xl max-h-[90vh] w-full relative" onClick={e => e.stopPropagation()} style={{
        isolation: 'isolate',
        position: 'relative',
        zIndex: 10000000
      }} data-id="element-20">
            {/* Schließen-Button */}
            <button className="absolute top-2 right-2 bg-gray-800 rounded-full p-2 text-white z-10 hover:bg-gray-700" onClick={closeModal} style={{
          zIndex: 10000001
        }} data-id="element-21">
              <X size={20} data-id="element-22" />
            </button>
            {/* Navigations-Buttons */}
            <button className={`absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-800 rounded-full p-3 text-white ${selectedIndex <= 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`} onClick={handlePrevImage} disabled={selectedIndex <= 0} style={{
          zIndex: 10000001
        }} data-id="element-23">
              <ChevronLeft size={24} data-id="element-24" />
            </button>
            <button className={`absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-800 rounded-full p-3 text-white ${selectedIndex >= sampleLabelImages.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`} onClick={handleNextImage} disabled={selectedIndex >= sampleLabelImages.length - 1} style={{
          zIndex: 10000001
        }} data-id="element-25">
              <ChevronRight size={24} data-id="element-26" />
            </button>
            {/* Bild-Container */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-xl" style={{
          position: 'relative',
          zIndex: 10000000
        }} data-id="element-27">
              <img src={selectedImage.imageUrl} alt={`Fass-Etikett ${selectedImage.barrelId}`} className="max-w-full max-h-[70vh] object-contain mx-auto" data-id="element-28" />
              <div className="mt-4 flex justify-between items-center" data-id="element-29">
                <div data-id="element-30">
                  <h4 className="text-xl font-bold text-white" data-id="element-31">
                    {selectedImage.barrelId}
                  </h4>
                  <p className="text-gray-400" data-id="element-32">
                    Erfasst um {selectedImage.timestamp} Uhr
                  </p>
                </div>
                <div className="text-gray-400" data-id="element-33">
                  {selectedIndex + 1} von {sampleLabelImages.length}
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};